import React, { useEffect, useState, useRef } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  collection, getDocs, query, doc, updateDoc, deleteDoc,
  addDoc, serverTimestamp, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Check, Package, Truck, NotePencil, Eye, EyeSlash, Trash } from '@phosphor-icons/react';

function ActionBtn({ icon, title, color, bg, onClick }) {
  return (
    <button onClick={onClick} title={title} style={{
      width: 30, height: 30, borderRadius: 7, border: 'none',
      background: bg || '#F1F5F9', cursor: 'pointer', color: color || '#64748B',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background 0.12s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = bg ? bg + 'dd' : '#E2E8F0'}
    onMouseLeave={e => e.currentTarget.style.background = bg || '#F1F5F9'}
    >{icon}</button>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

const toDate = (v) => {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  return new Date(v);
};

const fmtDate = (v) => {
  const d = toDate(v);
  return d ? d.toISOString().slice(0, 10) : '';
};

// Writes a PHIPA-required audit entry to activityLogs
const promptForInitials = (message) => {
  while (true) {
    const raw = window.prompt(message);
    if (raw === null) return null;
    const initials = raw.replace(/\s+/g, "").toUpperCase();
    if (/^[A-Z]{1,4}$/.test(initials)) return initials;
    window.alert("Enter 1-4 letters only.");
  }
};

const writeLog = async (pharmacyId, user, action, prev, next, staffInitials = "") => {
  try {
    await addDoc(collection(db, 'pharmacies', pharmacyId, 'activityLogs'), {
      userId: user.uid,
      displayName: user.displayName || user.email,
      licenceNumber: '',
      action,
      previousValue: prev ?? '',
      newValue: next ?? '',
        ...(staffInitials ? { staffInitials } : {}),
      timestamp: serverTimestamp(),
    });
  } catch (e) {
    console.error('activityLog write failed:', e);
  }
};

// ─── component ───────────────────────────────────────────────────────────────

export default function PatientsPage() {
  const { pharmacy, role, user } = useAuth();
  console.log("PHARMACY SETTINGS:", pharmacy);

  const [patients, setPatients]   = useState([]);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [noteModal, setNoteModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [editing, setEditing]     = useState(null);  // { id, cycleLength, lastPickup }
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [newPat, setNewPat]       = useState({ name: '', cycleLength: 28 });

  const [localCycles, setLocalCycles] = useState({});
  const [localDates, setLocalDates] = useState({});
  const canEdit = ['admin', 'pharmacist', 'superadmin', 'technician'].includes(role);

  // ── import state ────────────────────────────────────────────────────────
  const fileInputRef = useRef(null);
  const [importStep, setImportStep]       = useState(null); // null | 'mapping' | 'preview'
  const [importRawHeaders, setImportRawHeaders] = useState([]);
  const [importRows, setImportRows]       = useState([]);    // raw parsed rows
  const [importMapping, setImportMapping] = useState({});    // { name:'ColA', cycleLength:'ColB', lastPickup:'ColC' }
  const [importPreview, setImportPreview] = useState([]);    // mapped rows ready to write
  const [importError, setImportError]     = useState('');

  const IMPORT_FIELDS = [
    { key: 'name',        label: 'Patient Name',      required: true  },
    { key: 'cycleLength', label: 'Cycle Length (days)', required: false },
    { key: 'lastPickup',  label: 'Last Pickup Date',  required: false },
  ];

  // Auto-match headers by fuzzy name
  const autoMatch = (headers) => {
    const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const mapping = {};
    IMPORT_FIELDS.forEach(({ key }) => {
      const aliases = {
        name:        ['name', 'patient', 'patientname', 'fullname', 'lastname', 'firstname'],
        cycleLength: ['cycle', 'cyclelength', 'days', 'cycledays', 'length'],
        lastPickup:  ['lastpickup', 'pickup', 'lastpickupdate', 'pickupdate', 'lastdate', 'date'],
      }[key];
      const match = headers.find(h => aliases.some(a => norm(h).includes(a)));
      if (match) mapping[key] = match;
    });
    return mapping;
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    if (!file) return;
    setImportError('');
    const ext = file.name.split('.').pop().toLowerCase();

    const processRows = (rows) => {
      if (!rows || rows.length < 2) { setImportError('File appears empty.'); return; }
      const headers = rows[0].map(h => String(h ?? '').trim()).filter(Boolean);
      const dataRows = rows.slice(1).filter(r => r.some(c => c !== '' && c != null));
      setImportRawHeaders(headers);
      setImportRows(dataRows.map(r => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = String(r[i] ?? '').trim(); });
        return obj;
      }));
      setImportMapping(autoMatch(headers));
      setImportStep('mapping');
    };

    if (ext === 'csv') {
      Papa.parse(file, {
        complete: (res) => processRows(res.data),
        skipEmptyLines: true,
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const arr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        processRows(arr);
      };
      reader.readAsArrayBuffer(file);
    } else {
      setImportError('Unsupported file type. Use .csv, .xlsx, or .xls');
    }
  };

  const applyMapping = () => {
    if (!importMapping.name) { setImportError('You must map the Patient Name column.'); return; }
    setImportError('');
    const preview = importRows
      .map(row => ({
        name:        row[importMapping.name]        || '',
        cycleLength: parseInt(row[importMapping.cycleLength]) || 28,
        lastPickup:  importMapping.lastPickup ? row[importMapping.lastPickup] : '',
      }))
      .filter(r => r.name.trim());
    if (!preview.length) { setImportError('No valid patient rows found after mapping.'); return; }
    setImportPreview(preview);
    setImportStep('preview');
  };

  const confirmImport = async () => {
    let count = 0;
    for (const p of importPreview) {
      const lpDate = p.lastPickup ? new Date(p.lastPickup) : null;
      const cycle  = p.cycleLength || 28;
      let nd = null;
      if (lpDate && !isNaN(lpDate)) {
        const next = new Date(lpDate);
        next.setDate(next.getDate() + cycle);
        nd = Timestamp.fromDate(next);
      }
      await addDoc(collection(db, 'pharmacies', pharmacy.id, 'patients'), {
        name:            p.name.trim(),
        cycleLength:     cycle,
        lastPickup:      lpDate && !isNaN(lpDate) ? Timestamp.fromDate(lpDate) : null,
        nextDue:         nd,
        status:          'active',
        ready:           false,
        ignored:         false,
        deliveryPatient: false,
        createdAt:       serverTimestamp(),
        updatedAt:       serverTimestamp(),
        updatedBy:       user.uid,
        updatedByName:   user.email,
        updatedByRole:   role,
      });
      await writeLog(pharmacy.id, user, `Patient imported: ${p.name.trim()}`, '', 'active');
      count++;
    }
    setImportStep(null);
    setImportPreview([]);
    setImportRows([]);
    setImportRawHeaders([]);
    setImportMapping({});
    load();
    alert(`${count} patient(s) imported successfully.`);
  };

  const closeImport = () => {
    setImportStep(null);
    setImportPreview([]);
    setImportRows([]);
    setImportRawHeaders([]);
    setImportMapping({});
    setImportError('');
  };

  // ── load ──────────────────────────────────────────────────────────────────

  const load = async () => {
    if (!pharmacy) return;
    setLoading(true);
    const snap = await getDocs(collection(db, 'pharmacies', pharmacy.id, 'patients'));
    setPatients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { load(); }, [pharmacy]);

  // ── save inline edit ──────────────────────────────────────────────────────

  const saveField = async (p, newCycle, newPickup) => {
    try {
      const ref    = doc(db, 'pharmacies', pharmacy.id, 'patients', p.id);
      const cycle  = parseInt(newCycle) || p.cycleLength || 28;
      const lpDate = newPickup ? new Date(newPickup) : toDate(p.lastPickup);
      const lp     = lpDate && !isNaN(lpDate.getTime()) ? Timestamp.fromDate(lpDate) : (p.lastPickup ? p.lastPickup : deleteField());
      let nd = p.nextDue;
      if (lpDate) {
        const next = new Date(lpDate);
        next.setDate(next.getDate() + cycle);
        nd = Timestamp.fromDate(next);
      }
      const prevCycle  = p.cycleLength || 28;
      const prevPickup = fmtDate(p.lastPickup);
      await updateDoc(ref, {
        cycleLength: cycle, lastPickup: lp, nextDue: nd,
        updatedAt: serverTimestamp(), updatedBy: user.uid,
        updatedByName: user.email, updatedByRole: role,
      });
      const changes = [];
      if (cycle !== prevCycle)      changes.push(`cycle: ${prevCycle}d → ${cycle}d`);
      if (newPickup !== prevPickup) changes.push(`lastPickup: ${prevPickup} → ${newPickup}`);
      if (changes.length) {
        await writeLog(pharmacy.id, user, `Patient updated: ${p.name}`,
          changes.map(c => c.split(' → ')[0]).join(', '),
          changes.map(c => c.split(' → ')[1]).join(', '));
      }
      load();
    } catch (e) {
      console.error('saveField failed:', e);
      alert('Save failed: ' + (e.message || 'unknown error'));
    }
  };

  const save = async (p) => {
    const ref     = doc(db, 'pharmacies', pharmacy.id, 'patients', p.id);
    const cycle   = parseInt(editing.cycleLength) || p.cycleLength || 28;
    const lpDate  = editing.lastPickup ? new Date(editing.lastPickup) : toDate(p.lastPickup);
    const lp      = lpDate ? Timestamp.fromDate(lpDate) : p.lastPickup;

    let nd = p.nextDue;
    if (lpDate) {
      const next = new Date(lpDate);
      next.setDate(next.getDate() + cycle);
      nd = Timestamp.fromDate(next);
    }

    const prevCycle  = p.cycleLength || 28;
    const prevPickup = fmtDate(p.lastPickup);

    await updateDoc(ref, {
      cycleLength: cycle,
      lastPickup: lp,
      nextDue: nd,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: user.email,
      updatedByRole: role,
    });

    // PHIPA audit log
    const changes = [];
    if (cycle !== prevCycle)               changes.push(`cycle: ${prevCycle}d → ${cycle}d`);
    if (editing.lastPickup !== prevPickup) changes.push(`lastPickup: ${prevPickup} → ${editing.lastPickup}`);
    if (changes.length) {
      await writeLog(pharmacy.id, user, `Patient updated: ${p.name}`,
        changes.map(c => c.split(' → ')[0]).join(', '),
        changes.map(c => c.split(' → ')[1]).join(', '));
    }

    setEditing(null);
    load();
  };

  // ── toggle fields ─────────────────────────────────────────────────────────

  const markPickedUp = async (p) => {
    let staffInitials = "";

    if (pharmacy?.requirePickupInitials) {
      staffInitials = promptForInitials(`Enter staff initials before recording pickup for ${p.name}:`);
      if (staffInitials === null) return;
    }

    const today = Timestamp.fromDate(new Date());
    const next = new Date();
    next.setDate(next.getDate() + (p.cycleLength || 28));

    await updateDoc(doc(db, "pharmacies", pharmacy.id, "patients", p.id), {
      lastPickup: today,
      nextDue: Timestamp.fromDate(next),
      ready: false,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: user.email,
      updatedByRole: role,
    });

    await writeLog(
      pharmacy.id,
      user,
      `Patient marked picked up: ${p.name}`,
      p.lastPickup?.toDate?.()?.toISOString?.() || "",
      today.toDate().toISOString(),
      staffInitials
    );

    load();
  };

  const saveNote = async () => {
    if (!noteModal) return;
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', noteModal.id), {
      note: noteModal.note, updatedAt: serverTimestamp(),
      updatedBy: user.uid, updatedByName: user.email, updatedByRole: role,
    });
    await writeLog(pharmacy.id, user,
      `Patient note updated: ${noteModal.name}`, '', noteModal.note);
    setNoteModal(null);
    load();
    };

  const toggle = async (p, field) => {
    let staffInitials = "";

    if (field === "ready" && !p.ready && pharmacy?.requireReadyInitials) {
      staffInitials = promptForInitials(`Enter staff initials before marking ${p.name} Ready:`);
      if (staffInitials === null) return;
    }

    const newVal = !p[field];

    await updateDoc(doc(db, "pharmacies", pharmacy.id, "patients", p.id), {
      [field]: newVal,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: user.email,
      updatedByRole: role,
    });

    await writeLog(
      pharmacy.id,
      user,
      `Patient ${field} toggled: ${p.name}`,
      String(p[field] ?? false),
      String(newVal),
      staffInitials
    );

    load();
  };

  // ── add patient ───────────────────────────────────────────────────────────

  const addPatient = async () => {
    if (!newPat.name.trim()) return;
    await addDoc(collection(db, 'pharmacies', pharmacy.id, 'patients'), {
      name:            newPat.name.trim(),
      cycleLength:     parseInt(newPat.cycleLength) || 28,
      status:          'active',
      ready:           false,
      ignored:         false,
      deliveryPatient: false,
      createdAt:       serverTimestamp(),
      updatedAt:       serverTimestamp(),
      updatedBy:       user.uid,
      updatedByName:   user.email,
      updatedByRole:   role,
    });
    await writeLog(pharmacy.id, user, `Patient added: ${newPat.name.trim()}`, '', 'active');
    setNewPat({ name: '', cycleLength: 28 });
    setShowAdd(false);
    load();
  };

  // ── filter + search ───────────────────────────────────────────────────────

  const now = new Date();
  const displayed = patients
    .filter(p => {
      if (filter === 'active')   return p.status === 'active' && !p.ignored;
      if (filter === 'ready')    return p.ready;
      if (filter === 'delivery') return p.deliveryPatient;
      if (filter === 'ignored')  return p.ignored;
      return true;
    })
    .filter(p => {
      const nd2 = toDate(p.nextDue); const now2 = new Date(); const days2 = nd2 ? Math.ceil((nd2 - now2) / 86400000) : null;
      if (statusFilter === 'overdue') return nd2 && nd2 < now2 && !p.ready;
      if (statusFilter === 'prepnow') return !p.ready && nd2 && nd2 >= now2 && days2 !== null && days2 <= 3;
      if (statusFilter === 'ready')   return p.ready;
      if (statusFilter === 'ontrack') return !p.ready && nd2 && nd2 >= now2 && days2 !== null && days2 > 3;
      return true;
    })
    .filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()));

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0F172A' }}>Patients</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94A3B8' }}>{displayed.length} patients</p>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
            <button onClick={() => fileInputRef.current?.click()} style={{
              padding: '9px 20px', background: '#0F6B3F', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>⬆ Import CSV/Excel</button>
            <button onClick={() => setShowAdd(true)} style={{
              padding: '9px 20px', background: '#0F172A', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>+ Add Patient</button>
          </div>
        )}
      </div>

      {/* Add Patient Modal */}
      {showAdd && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 32, width: 380,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Add Patient</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
                Full Name (Last, First)
              </label>
              <input value={newPat.name}
                onChange={e => setNewPat({ ...newPat, name: e.target.value })}
                onBlur={e => {
                  const val = e.target.value.trim();
                  const titled = val.split(',').map(part => part.trim().split(' ').map(w => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : '').join(' ')).join(', ');
                  setNewPat(n => ({ ...n, name: titled }));
                }}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB',
                  borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
                Cycle Length (days)
              </label>
              <select value={newPat.cycleLength}
                onChange={e => setNewPat({ ...newPat, cycleLength: e.target.value })}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB',
                  borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}>
                {[14, 21, 28, 56, 84, 168].map(v => <option key={v} value={v}>{v} days</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={addPatient} style={{
                flex: 1, padding: 10, background: '#0F172A', color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
              }}>Add Patient</button>
              <button onClick={() => setShowAdd(false)} style={{
                flex: 1, padding: 10, background: '#F1F5F9', color: '#0F172A',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Import: Column Mapping Modal ── */}
      {importStep === 'mapping' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 36, width: 480,
            boxShadow: '0 24px 64px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700 }}>Map Columns</h3>
            <p style={{ margin: '0 0 24px', fontSize: 13, color: '#64748B' }}>
              Match your file columns to BlisterFlow fields. Auto-matched where possible.
            </p>
            {IMPORT_FIELDS.map(({ key, label, required }) => (
              <div key={key} style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600,
                  color: '#374151', marginBottom: 5 }}>
                  {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
                </label>
                <select
                  value={importMapping[key] || ''}
                  onChange={e => setImportMapping({ ...importMapping, [key]: e.target.value || undefined })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB',
                    borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}>
                  <option value="">— not mapped —</option>
                  {importRawHeaders.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            ))}
            {importError && <p style={{ color: '#EF4444', fontSize: 13, marginBottom: 12 }}>{importError}</p>}
            <div style={{ marginTop: 8, padding: '10px 14px', background: '#F8FAFC',
              borderRadius: 8, fontSize: 12, color: '#64748B' }}>
              <strong>Detected columns:</strong> {importRawHeaders.join(', ')}
              <br/><strong>Data rows found:</strong> {importRows.length}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={applyMapping} style={{
                flex: 1, padding: 10, background: '#0F172A', color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                Preview Import →
              </button>
              <button onClick={closeImport} style={{
                flex: 1, padding: 10, background: '#F1F5F9', color: '#0F172A',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Import: Preview & Confirm Modal ── */}
      {importStep === 'preview' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 36, width: 620,
            boxShadow: '0 24px 64px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700 }}>
              Preview — {importPreview.length} Patients
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748B' }}>
              Review before importing. Rows with no name are excluded.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F8FAFC' }}>
                    {['Name', 'Cycle (days)', 'Last Pickup'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left',
                        fontWeight: 600, color: '#374151', borderBottom: '1px solid #E2E8F0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importPreview.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '7px 12px' }}>{r.name}</td>
                      <td style={{ padding: '7px 12px' }}>{r.cycleLength}</td>
                      <td style={{ padding: '7px 12px' }}>{r.lastPickup || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button onClick={confirmImport} style={{
                flex: 1, padding: 10, background: '#0F6B3F', color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                ✓ Confirm Import
              </button>
              <button onClick={() => setImportStep('mapping')} style={{
                padding: '10px 18px', background: '#F1F5F9', color: '#0F172A',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                ← Back
              </button>
              <button onClick={closeImport} style={{
                padding: '10px 18px', background: '#F1F5F9', color: '#0F172A',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {['all', 'active', 'ready', 'delivery', 'ignored'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
            border: '1px solid',
            borderColor: filter === f ? '#0F172A' : '#E2E8F0',
            background:  filter === f ? '#0F172A' : '#fff',
            color:       filter === f ? '#fff'    : '#64748B',
            cursor: 'pointer', textTransform: 'capitalize',
          }}>{f}</button>
        ))}
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, border: '1px solid #E2E8F0', background: '#fff', color: '#64748B', cursor: 'pointer' }}>
          <option value="all">All Statuses</option>
          <option value="overdue">Overdue</option>
          <option value="prepnow">Prep Now</option>
          <option value="ready">Ready</option>
          <option value="ontrack">On Track</option>
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search patients…"
          style={{
            padding: '6px 14px', border: '1px solid #E2E8F0', borderRadius: 20,
            fontSize: 13, outline: 'none', minWidth: 200, marginLeft: 'auto',
          }} />
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>No patients match this filter.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Patient', 'Cycle', 'Last Pickup', 'Next Due', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: h === 'Patient' ? 'left' : 'center', fontSize: 11,
                    fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderBottom: '1px solid #F1F5F9',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map(p => {
                const nd       = toDate(p.nextDue);
                const isOD     = nd && nd < now && !p.ready;
                const daysUntil = nd ? Math.ceil((nd - now) / (1000 * 60 * 60 * 24)) : null;
                const isPrepNow = !p.ready && !isOD && daysUntil !== null && daysUntil <= 3;
                return (
                  <tr key={p.id} style={{ borderBottom: '1px solid #F8FAFC' }}>

                    {/* Name */}
                    <td style={{ padding: '11px 16px', fontWeight: 600, color: '#0F172A' }}>{p.name}</td>

                    {/* Cycle */}
                    <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                      {canEdit ? (
                        <select value={p.cycleLength || 28}
                          onChange={e => { setLocalCycles(prev => ({ ...prev, [p.id]: parseInt(e.target.value) })); if (p.lastPickup) saveField(p, e.target.value, null); }}
                          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 13, background: '#F8FAFC', cursor: 'pointer', display: 'block', margin: '0 auto' }}>
                          {[7, 14, 28, 56, 84, 168].map(v => <option key={v} value={v}>{v} days</option>)}
                        </select>
                      ) : `${p.cycleLength || 28} days`}
                    </td>

                    {/* Last Pickup */}
                    <td style={{ padding: '11px 16px', color: '#64748B', textAlign: 'center' }}>
                      {canEdit ? (
                        <input type="date" value={localDates[p.id] ?? fmtDate(p.lastPickup) ?? ''}
                          onChange={e => { const val = e.target.value; setLocalDates(prev => ({ ...prev, [p.id]: val })); if (val) saveField(p, localCycles[p.id] ?? p.cycleLength ?? 28, val); }}
                          onBlur={e => { const val = e.target.value; if (val) saveField(p, localCycles[p.id] ?? p.cycleLength ?? 28, val); }}
                          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 13, background: '#F8FAFC', cursor: 'pointer', display: 'block', margin: '0 auto' }} />
                      ) : fmtDate(p.lastPickup) || '—'}
                    </td>

                    {/* Next Due */}
                    <td style={{ padding: '11px 16px', color: '#64748B', textAlign: 'center' }}>{fmtDate(p.nextDue) || '—'}</td>

                    {/* Status */}
                    <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: p.ready ? '#DCFCE7' : isOD ? '#FEE2E2' : isPrepNow ? '#FEF3C7' : '#E0F2FE',
                        color:      p.ready ? '#16A34A' : isOD ? '#DC2626' : isPrepNow ? '#D97706' : '#0369A1',
                      }}>
                        {p.ready ? 'Ready' : isOD ? 'Overdue' : isPrepNow ? 'Prep Now' : 'On Track'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '6px 16px', textAlign: 'center' }}>
                      {canEdit && (
                        <div style={{ display: 'flex', gap: 3 }}>
                          <ActionBtn icon={<Check size={14} weight="bold" />}
                            title={p.ready ? 'Unmark Ready' : 'Mark Ready'}
                            color={p.ready ? '#D97706' : '#16A34A'}
                            bg={p.ready ? '#FEF9C3' : '#DCFCE7'}
                            onClick={() => toggle(p, 'ready')} />
                          <ActionBtn icon={<Package size={14} weight="bold" />}
                            title="Picked up today"
                            color="#1768C4" bg="#DBEAFE"
                            onClick={() => markPickedUp(p)} />
                          <ActionBtn icon={<Truck size={14} weight="bold" />}
                            title="Toggle delivery"
                            color={p.deliveryPatient ? '#1768C4' : '#64748B'}
                            bg={p.deliveryPatient ? '#DBEAFE' : '#F1F5F9'}
                            onClick={() => toggle(p, 'deliveryPatient')} />
                          <ActionBtn icon={<NotePencil size={14} weight="bold" />}
                            title="Add note"
                            color={p.note ? '#D97706' : '#64748B'}
                            bg={p.note ? '#FEF3C7' : '#F1F5F9'}
                            onClick={() => setNoteModal({ id: p.id, name: p.name, note: p.note || '' })} />
                          <ActionBtn icon={p.ignored ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}
                            title={p.ignored ? 'Unignore' : 'Ignore'}
                            color={p.ignored ? '#DC2626' : '#94A3B8'}
                            bg={p.ignored ? '#FEE2E2' : '#F1F5F9'}
                            onClick={() => toggle(p, 'ignored')} />
                          <ActionBtn icon={<Trash size={14} weight="bold" />}
                            title="Delete patient"
                            color="#DC2626" bg="#FEE2E2"
                            onClick={() => setDeleteConfirm({ id: p.id, name: p.name })} />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  return (
    <>
      {content_placeholder}
      {noteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Patient Note</h3>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748B' }}>{noteModal.name}</p>
            <textarea
              value={noteModal.note}
              onChange={e => setNoteModal({ ...noteModal, note: e.target.value })}
              placeholder="Add a note about this patient..."
              style={{ width: '100%', minHeight: 100, padding: '10px 12px', borderRadius: 8,
                border: '1px solid #E2E8F0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setNoteModal(null)} style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid #E2E8F0',
                background: '#F8FAFC', color: '#64748B', fontSize: 13, cursor: 'pointer'
              }}>Cancel</button>
              <button onClick={saveNote} style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: '#1768C4', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600
              }}>Save Note</button>
            </div>
          </div>
        </div>
      )}
      {deleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#0F172A' }}>Delete Patient</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748B' }}>
              Are you sure you want to permanently delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid #E2E8F0',
                background: '#F8FAFC', color: '#64748B', fontSize: 13, cursor: 'pointer'
              }}>Cancel</button>
              <button onClick={deletePatient} style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: '#DC2626', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600
              }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}